import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ImageOff, ArrowRight, Hammer, X, Loader2 } from 'lucide-react';
import { getApiUrl } from '../lib/api';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import Lightbox from '../components/Lightbox';
import { Seo } from '../components/Seo';

const FILTERS = [
  'Tous',
  'Appartement',
  'Maison',
  'Rénovation intérieure',
  'Rénovation énergétique',
  'Ravalement / Façade',
  'Copropriété',
];

const PHASE_LABELS = { before: 'Avant', during: 'Pendant', after: 'Après' };

const PhotoGroup = ({ phase, photos, onPhotoClick }) => {
  if (!photos || photos.length === 0) return null;
  return (
    <div className="mb-6">
      <h4 className="text-sm font-semibold text-emerald-700 uppercase tracking-wide mb-3">
        {PHASE_LABELS[phase]}
      </h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onPhotoClick(p.id)}
            data-testid={`realisation-photo-${p.id}`}
            className="rounded-xl overflow-hidden border border-slate-200 aspect-[4/3] cursor-zoom-in group focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <img
              src={`${getApiUrl()}${p.url}`}
              alt={PHASE_LABELS[phase]}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const Realisations = () => {
  const [active, setActive] = useState('Tous');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${getApiUrl()}/api/realisations`);
        if (res.ok) {
          const data = await res.json();
          setItems(data.realisations || []);
        }
      } catch {
        /* reste en état vide */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = items.filter((r) => active === 'Tous' || r.category === active);

  const lightboxPhotos = selected
    ? [
        ...(selected.photos?.before || []).map((p) => ({ ...p, url: `${getApiUrl()}${p.url}`, label: 'Avant' })),
        ...(selected.photos?.during || []).map((p) => ({ ...p, url: `${getApiUrl()}${p.url}`, label: 'Pendant' })),
        ...(selected.photos?.after || []).map((p) => ({ ...p, url: `${getApiUrl()}${p.url}`, label: 'Après' })),
      ]
    : [];

  const openLightbox = (id) => {
    const idx = lightboxPhotos.findIndex((p) => p.id === id);
    if (idx >= 0) setLightboxIndex(idx);
  };

  const closeModal = () => {
    setSelected(null);
    setLightboxIndex(null);
  };

  return (
    <main className="pt-28" data-testid="page-realisations">
      <Seo
        title="Nos réalisations de rénovation | VIREO"
        description="Découvrez les chantiers de rénovation, ravalement et toiture réalisés par VIREO à Paris et en Île-de-France : photos avant / pendant / après."
        path="/realisations"
      />
      {/* Header */}
      <section className="bg-slate-900 py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider mb-3 block">
              Nos réalisations
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
              Nos chantiers de rénovation
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
              Appartements, maisons, ravalements et rénovations énergétiques réalisés à Paris et
              en Île-de-France. Découvrez nos avant / pendant / après.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-10 bg-white border-b border-slate-100 sticky top-[92px] z-30">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-3">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setActive(f)}
                data-testid={`realisation-filter-${f}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                  active === f
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-500 hover:text-emerald-700'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-slate-50 min-h-[45vh] pb-28 md:pb-20">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600 mx-auto mb-3" />
              <p className="text-slate-600">Chargement des réalisations…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div
              data-testid="realisations-empty"
              className="max-w-2xl mx-auto text-center bg-white border border-slate-200 rounded-3xl p-10 md:p-16 shadow-sm"
            >
              <div className="w-20 h-20 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <ImageOff className="h-10 w-10 text-emerald-500" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                {items.length === 0
                  ? 'Premières réalisations bientôt publiées'
                  : 'Aucune réalisation dans cette catégorie'}
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                {items.length === 0
                  ? "Nous préparons une sélection de nos chantiers avec photos avant / pendant / après. En attendant, parlez-nous de votre projet — nous serons ravis d'y répondre."
                  : 'Essayez un autre filtre ou parlez-nous de votre projet.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/#contact">
                  <Button className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white px-7 py-6 text-base" data-testid="realisations-contact-cta">
                    Demander un devis gratuit
                  </Button>
                </Link>
                <Link to="/partenaires-professionnels">
                  <Button variant="outline" className="border-slate-300 px-7 py-6 text-base" data-testid="realisations-partner-cta">
                    <Hammer className="h-4 w-4 mr-2" /> Je suis un professionnel
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelected(r)}
                  data-testid={`realisation-item-${r.id}`}
                  className="text-left rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-sm hover:shadow-xl hover:border-emerald-300 transition-all group"
                >
                  <div className="h-56 overflow-hidden bg-slate-100">
                    {r.cover_url ? (
                      <img
                        src={`${getApiUrl()}${r.cover_url}`}
                        alt={r.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageOff className="h-8 w-8 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">{r.category}</span>
                    <h3 className="text-lg font-bold text-slate-900 mt-1">{r.title}</h3>
                    {r.location && <p className="text-sm text-slate-600 mt-1">{r.location}</p>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Detail Modal */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[60]"
          onClick={closeModal}
          data-testid="realisation-modal"
        >
          <div
            className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-6 border-b border-slate-100 sticky top-0 bg-white rounded-t-3xl">
              <div>
                <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">{selected.category}</span>
                <h3 className="text-2xl font-bold text-slate-900">{selected.title}</h3>
                {selected.location && <p className="text-slate-500">{selected.location}</p>}
              </div>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-700 flex-shrink-0"
                data-testid="realisation-modal-close"
                aria-label="Fermer"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              {selected.description && (
                <p className="text-slate-600 leading-relaxed mb-6">{selected.description}</p>
              )}
              {selected.photos?.before?.[0] && selected.photos?.after?.[0] && (
                <div className="mb-8">
                  <h4 className="text-sm font-semibold text-emerald-700 uppercase tracking-wide mb-3">
                    Comparatif avant / après
                  </h4>
                  <BeforeAfterSlider
                    beforeUrl={`${getApiUrl()}${selected.photos.before[0].url}`}
                    afterUrl={`${getApiUrl()}${selected.photos.after[0].url}`}
                  />
                  <p className="text-xs text-slate-400 mt-2 text-center">Glissez le curseur pour comparer</p>
                </div>
              )}
              <PhotoGroup phase="before" photos={selected.photos?.before} onPhotoClick={openLightbox} />
              <PhotoGroup phase="during" photos={selected.photos?.during} onPhotoClick={openLightbox} />
              <PhotoGroup phase="after" photos={selected.photos?.after} onPhotoClick={openLightbox} />
              <div className="mt-6 pt-6 border-t border-slate-100 text-center">
                <Link to="/#contact">
                  <Button className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white px-7 py-6">
                    Un projet similaire ? Demander un devis
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen photo gallery */}
      {selected && lightboxIndex !== null && (
        <Lightbox
          images={lightboxPhotos}
          index={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {/* Bottom CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <p className="text-lg text-slate-600 mb-4">Un projet similaire en tête ?</p>
          <Link to="/#contact" className="inline-flex items-center gap-2 text-emerald-700 font-semibold text-lg hover:gap-3 transition-all">
            Lancer mon projet <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Realisations;
